import React from "react";
import { listAllowedChatAttachmentMimes } from "@intelligems/ig-utils/chat-attachment-validation";
import { UserInput as UserInputType } from "@intelligems/ig-types/schemas/ai/node-execution";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { InterruptedTurnNotice } from "@src/features/ai/components/WorkflowEditor/execution/panel/InterruptedTurnNotice";
import { SurveyCard } from "@src/features/ai/components/WorkflowEditor/execution/panel/SurveyCard";
import {
  composerPlaceholder,
  isCardAsk,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyAskState";
import { useIsTextRevealing } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/useIsTextRevealing";
import { PromptComposer } from "./PromptComposer";
import { AttachmentPreview } from "@src/features/ai/components/WorkflowEditor/execution/upload/AttachmentPreview";
import { AddAttachmentsButton } from "@src/features/ai/components/WorkflowEditor/execution/upload/AddAttachmentsButton";

/**
 * Adapts `ExecutionPanelContext` to `PromptComposer`'s plain-prop interface —
 * the composer used by every execution-panel-based AI chat. Consumers that
 * don't have (or want) an `ExecutionPanelContext` — e.g.
 * `AskAtlasBuildExperience`, which owns its own local turn state instead of a
 * workflow-backed `aiChat` — should render `PromptComposer` directly instead
 * of this wrapper.
 *
 * Appearance comes from `slotProps.userInput`, which is the composer's own
 * slot props, read off
 * the context here so nothing has to be drilled through `ExecutionPanel`'s
 * props. Absent, every chat renders exactly as before.
 */
export const UserInput = () => {
  const revealing = useIsTextRevealing();
  const {
    aiChat,
    getSlot,
    getSlotProps,
    onSubmit,
    streaming,
    interrupted,
    disabled,
    onSend,
    userInputMessage,
    setUserInputMessage,
    fileUpload,
    placeholder,
    inputRequirement,
    survey,
  } = useExecutionPanelContext();

  const handleChange = (value: string) =>
    setUserInputMessage({ type: "text", content: value });

  const cardAskParked = isCardAsk(inputRequirement);
  const submitDisabled = cardAskParked && revealing;
  const currentIndex = survey?.currentIndex ?? 0;

  // The card is held back while the question is still being written, so its
  // rows only count once it is actually on screen.
  const cardOnScreen = cardAskParked && !revealing;

  // While a file ask's card is on screen it owns the picked files — chips with
  // their own remove buttons — so the composer's tile strip would render the
  // same list a second time.
  const cardOwnsAttachments =
    inputRequirement.userInput.type === "file" && !revealing;

  return (
    <PromptComposer
      value={getTextValue(userInputMessage)}
      onChange={handleChange}
      onSubmit={onSubmit}
      disabled={disabled}
      submitDisabled={submitDisabled}
      streaming={streaming}
      onStop={onSend.stop}
      error={aiChat.error?.message}
      accept={
        fileUpload ? listAllowedChatAttachmentMimes().join(",") : undefined
      }
      maxFiles={fileUpload?.maxPerTurn}
      promptInputPreview={
        cardOwnsAttachments
          ? null
          : getSlot(
              "promptInputPreview",
              fileUpload ? <AttachmentPreview /> : null
            )
      }
      promptInputTools={
        <>
          {fileUpload && <AddAttachmentsButton disabled={disabled} />}
          {getSlot("promptInputTools")}
        </>
      }
      // An interrupted turn takes the surface's own ask's place: Continue is
      // the one thing to offer until the turn is settled or superseded.
      composerAsk={
        interrupted ? <InterruptedTurnNotice /> : getSlot("composerAsk")
      }
      suggestions={getSlot(
        "suggestions",
        <>
          {getSlot("composerSuggestions")}
          <SurveyCard />
        </>
      )}
      // Tied to the pending question: "Or type your own answer..." only
      // while its option rows are on screen (see `composerPlaceholder`).
      placeholder={composerPlaceholder({
        requirement: inputRequirement,
        currentIndex,
        optionsOnScreen: cardOnScreen,
        fallback: placeholder,
      })}
      submit={getSlot("submit")}
      slotProps={getSlotProps("userInput")}
    />
  );
};

const getTextValue = (input: UserInputType | null) => {
  if (!input) {
    return "";
  }
  if (input.type === "text") {
    return input.content;
  }
  if (input.type === "choice") {
    return input.label ?? input.choice;
  }
  if (input.type === "multi-choice") {
    return input.choices.map((option) => option.label).join(", ");
  }
  if (input.type === "confirmation") {
    return input.confirmed ? "Yes" : "No";
  }
  // A structured answers reply submits via override input and never sits in the draft.
  return "";
};
